import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faInfoSquare,
    faInfoCircle,
    faExclamationTriangle,
    faTimesCircle,
    faTimesSquare,
    faTimesHexagon,
    faTimesOctagon,
    faTimes,
    faMegaphone,
    faSignIn,
    faSignOut,
    faHome,
    faDna,
    faMagnifyingGlass,
    faCogs,
    faUser,
    faSitemap,
    faHospitalAlt,
    faIdBadge,
    faExternalLink,
    faClipboardList,
    faList,
    faCheck,
    faCloudDownload,
    faStickyNote,
    faPerson,
    faLock,
    faQuestion,
    faMonitorWaveform,
    faComment,
    faFlaskVial,
    faBuilding,
    faAsterisk,
    faTrashCanUndo,
    faChevronDown,
    faChevronUp,
    faTrashCan,
    faQuestionSquare,
    faClipboardClock,
    faPencil,
    faEllipsisVertical,
} from "@fortawesome/duotone-regular-svg-icons";
import type { GetAppSettingsResponse, SentrySettings } from "./api/Tmd.dtos";
import type { GroupedFieldGridColumnSpanOptions } from "@ngt-form/mantine";
import type { GridColProps } from "@mantine/core";

export interface IClientSettings {
    baseUrl?: string;
    basePath?: string;
}

interface IDataGridSettings {
    filterDebounceMs: number;
}

interface IFormsGroupedFieldSettings {
    longFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
    shortFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
    sideBySideFieldColumnSpans: GroupedFieldGridColumnSpanOptions;
}

interface IFormsSettings {
    validationDebounceMs: number;
    groupedFields: IFormsGroupedFieldSettings;
    sideBySideColumnSpans: {
        columnOne: GridColProps["span"];
        columnTwo: GridColProps["span"];
    };
}

interface IErrorIconSettings {
    description: IconDefinition;
    information: IconDefinition;
    warning: IconDefinition;
    low: IconDefinition;
    medium: IconDefinition;
    high: IconDefinition;
    critical: IconDefinition;
}

interface IIconSettings {
    activate: IconDefinition;
    arrowUp: IconDefinition;
    arrowDown: IconDefinition;
    comments: IconDefinition;
    dashboard: IconDefinition;
    deactivate: IconDefinition;
    delete: IconDefinition;
    department: IconDefinition;
    download: IconDefinition;
    edit: IconDefinition;
    error: IErrorIconSettings;
    externalLink: IconDefinition;
    group: IconDefinition;
    information: IconDefinition;
    institution: IconDefinition;
    locked: IconDefinition;
    login: IconDefinition;
    logout: IconDefinition;
    lookup: IconDefinition;
    menu: IconDefinition;
    noAccess: IconDefinition;
    note: IconDefinition;
    participant: IconDefinition;
    previousVersion: IconDefinition;
    question: IconDefinition;
    reportABug: IconDefinition;
    required: IconDefinition;
    search: IconDefinition;
    settings: IconDefinition;
    success: IconDefinition;
    treatment: IconDefinition;
    trial: IconDefinition;
    trialItem: IconDefinition;
    trialTracker: IconDefinition;
    unknown: IconDefinition;
    user: IconDefinition;
}

interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}

interface IAppSettings {
    client: IClientSettings;
    dataGrid: IDataGridSettings;
    debug: boolean;
    environment: "Development" | "Test" | "Production";
    forms: IFormsSettings;
    icons: IIconSettings;
    sentry: ISentrySettings;
}

declare global {
    var APP_SETTINGS: GetAppSettingsResponse | undefined;
}

const ClientAppSettingsResponse: GetAppSettingsResponse | undefined = globalThis.APP_SETTINGS;

const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined,
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }
    const value: TData | undefined = ClientAppSettingsResponse?.appSettings?.sentrySettings?.[
        appSettingProperty
    ] as TData;
    if (value === undefined || value === null) {
        return undefined;
    }
    return value;
};

const getSentryEnvironmentValue = (environmentProperty: string | undefined): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    const value: string | undefined = import.meta.env[environmentProperty];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value;
};

const getSentryEnvironmentString = (
    environmentProperty: string | undefined,
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};

const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined,
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    try {
        return parseFloat(valueStr);
    } catch {
        return undefined;
    }
};

const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined,
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    const lowerCase = valueStr.toLowerCase();
    if (lowerCase === "1" || lowerCase === "true") {
        return true;
    }
    if (lowerCase === "0" || lowerCase === "false") {
        return false;
    }
    return undefined;
};

const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined,
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);
    if (appSettingValue) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentString(environmentProperty);
    if (environmentValue) {
        return environmentValue;
    }
    return undefined;
};

const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined,
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentNumber(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};

const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined,
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};

const AppSettings: IAppSettings = {
    client: {
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: undefined,
    },
    dataGrid: {
        filterDebounceMs: 250,
    },
    debug: import.meta.env.DEV,
    environment: "Development",
    forms: {
        validationDebounceMs: 500,
        groupedFields: {
            longFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3,
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 9,
                    xl: 9,
                },
            },
            shortFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3,
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 3,
                    xl: 3,
                },
            },
            sideBySideFieldColumnSpans: {
                label: {
                    base: 12,
                    md: 6,
                    lg: 6,
                    xl: 6,
                },
                field: {
                    base: 12,
                    md: 6,
                    lg: 6,
                    xl: 6,
                },
            },
        },
        sideBySideColumnSpans: {
            columnOne: {
                base: 12,
                md: 12,
                lg: 6,
                xl: 6,
            },
            columnTwo: {
                base: 12,
                md: 12,
                lg: 6,
                xl: 6,
            },
        },
    },
    icons: {
        activate: faTrashCanUndo,
        arrowDown: faChevronDown,
        arrowUp: faChevronUp,
        comments: faComment,
        dashboard: faHome,
        deactivate: faTrashCan,
        delete: faTimes,
        department: faBuilding,
        download: faCloudDownload,
        edit: faPencil,
        error: {
            description: faInfoSquare,
            information: faInfoCircle,
            warning: faExclamationTriangle,
            low: faTimesCircle,
            medium: faTimesSquare,
            high: faTimesHexagon,
            critical: faTimesOctagon,
        },
        externalLink: faExternalLink,
        group: faSitemap,
        information: faInfoCircle,
        institution: faHospitalAlt,
        locked: faLock,
        login: faSignIn,
        logout: faSignOut,
        lookup: faList,
        menu: faEllipsisVertical,
        noAccess: faIdBadge,
        note: faStickyNote,
        participant: faPerson,
        previousVersion: faClipboardClock,
        question: faQuestionSquare,
        reportABug: faMegaphone,
        required: faAsterisk,
        search: faMagnifyingGlass,
        settings: faCogs,
        success: faCheck,
        treatment: faFlaskVial,
        trial: faDna,
        trialItem: faClipboardList,
        trialTracker: faMonitorWaveform,
        unknown: faQuestion,
        user: faUser,
    },
    sentry: {
        dsn: getSentryString("dsn", "VITE_SENTRY_DSN"),
        debug: getSentryBoolean("debug", "VITE_SENTRY_DEBUG") ?? false,
        enableMetrics: getSentryBoolean("enableMetrics", "VITE_SENTRY_ENABLE_METRICS") ?? false,
        sendDefaultPii: getSentryBoolean("sendDefaultPii", "VITE_SENTRY_SEND_DEFAULT_PII") ?? false,
        attachStacktrace:
            getSentryBoolean("attachStacktrace", "VITE_SENTRY_ATTACH_STACKTRACE") ?? false,
        customer: getSentryString("customer", "VITE_SENTRY_CUSTOMER") ?? "Unknown",
        environment: getSentryString("environment", "VITE_SENTRY_ENVIRONMENT") ?? "Development",
        application:
            getSentryString(undefined, "VITE_SENTRY_APPLICATION") ?? "SourceDataVerification",
        release: getSentryString(undefined, "VITE_SENTRY_RELEASE"),
        enableLogs: getSentryBoolean("enableLogs", "VITE_SENTRY_ENABLE_LOGS") ?? false,
        replaysOnErrorSampleRate:
            getSentryNumber(
                "replaysOnErrorSampleRate",
                "VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE",
            ) ?? 1.0,
        replaysSessionSampleRate:
            getSentryNumber("replaysSessionSampleRate", "VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE") ??
            0.1,
        tracesSampleRate:
            getSentryNumber("tracesSampleRate", "VITE_SENTRY_TRACE_SAMPLE_RATE") ?? 0.2,
        enableReportABug:
            getSentryBoolean("enableReportABug", "VITE_SENTRY_ENABLE_REPORT_A_BUG") ?? false,
    },
};

export default AppSettings;
